import React, { useContext, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import { Editor } from 'react-draft-wysiwyg';
import {
  ContentState,
  convertFromHTML,
  convertToRaw,
  EditorState,
} from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import InputWrapper from '../../../../components/forms/Input/inputWrapper';
import { Input } from '../../../../components/forms';
import Button from '../../../../components/common/Button';
import { ACTION } from '../../../../reducer/projectReducer';
import { Link, useHistory } from 'react-router-dom';
import { ProjectContext } from '../../projectContext';
import { Modal } from '@material-ui/core';
import {
  CalendarTodayOutlined,
  InfoOutlined,
  WebOutlined,
} from '@material-ui/icons';
import ModalContent from '../../../../components/common/Modal/ModalContent';
import Alert from '../../../../components/common/Alert';
import InputColor from '../../../../components/forms/Input/InputColor';
import openAnnouncementWebPreview from '../preview/openAnnouncementWebPreview';
import AvailableRolesSelect from '../../../../components/forms/AvailableRolesSelect';
import { normalizeAvailableRoles } from '../../../../utils/registrationRoles';
import {
  DEFAULT_ANNOUNCEMENT_LOGO,
  getAnnouncementLogo,
} from '../../../../utils/announcementLogo';

import {
  ALLOWED_CHAR,
  PROGRAM_DESCRIPTION_MAX_LENGTH,
  MOBILE_WELCOME_MESSAGE_MAX_LENGTH,
  landingSchema,
} from '../../schemas/landing';

const createEditorStateFromHtml = (html = '') => {
  if (!html) return EditorState.createEmpty();

  try {
    const { contentBlocks, entityMap } = convertFromHTML(html);
    if (!contentBlocks?.length) return EditorState.createEmpty();

    return EditorState.createWithContent(
      ContentState.createFromBlockArray(contentBlocks, entityMap)
    );
  } catch (_error) {
    return EditorState.createEmpty();
  }
};

const serializeEditorState = (editorState) => {
  const content = editorState.getCurrentContent();
  return content.hasText() ? draftToHtml(convertToRaw(content)) : '';
};

const LandingForm = ({ onNext }) => {
  const history = useHistory();
  const [state, dispatch] = useContext(ProjectContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { client, configuration, landing, login, status } = state;
  const announcementLogo = getAnnouncementLogo(
    { ...landing, configuration },
    client
  );
  const hasCustomLogo =
    typeof configuration?.platform?.logo === 'string' &&
    Boolean(configuration.platform.logo.trim());
  const [isLoading, setIsLoading] = useState(false);
  const [messageAlert, setMessageAlert] = useState({ type: '', message: '' });
  const [aboutEditor, setAboutEditor] = useState(() =>
    createEditorStateFromHtml(landing?.about)
  );

  const initialValues = useMemo(
    () => ({
      ...landing,
      configuration: {
        ...(configuration || {}),
        registration: {
          ...(configuration?.registration || {}),
          availableRoles: normalizeAvailableRoles(
            configuration?.registration?.availableRoles
          ),
        },
      },
      loginPrimaryColor: login?.loginPrimaryColor || '',
      loginSecondaryColor: login?.loginSecondaryColor || '',
      mobileWelcomeMessage:
        landing?.configuration?.login?.mobileWelcomeMessage ||
        login?.mobileWelcomeMessage ||
        '',
    }),
    [
      configuration,
      landing,
      login?.loginPrimaryColor,
      login?.loginSecondaryColor,
      login?.mobileWelcomeMessage,
    ]
  );

  const getResolvedValues = (values) => {
    const {
      loginPrimaryColor,
      loginSecondaryColor,
      mobileWelcomeMessage,
      configuration: selectedConfiguration,
      ...landingValues
    } = values;
    const selectedRegistration = selectedConfiguration?.registration || {};
    const about = serializeEditorState(aboutEditor);

    return {
      ...landingValues,
      about,
      mobileWelcomeMessage,
      loginPrimaryColor,
      loginSecondaryColor,
      configuration: {
        ...(selectedConfiguration || {}),
        registration: {
          ...selectedRegistration,
          availableRoles: normalizeAvailableRoles(
            selectedRegistration.availableRoles
          ),
        },
      },
      landing: {
        ...landingValues,
        about,
      },
      login: {
        ...login,
        mobileWelcomeMessage,
        loginPrimaryColor,
        loginSecondaryColor,
      },
    };
  };

  const getLandingPayload = (values) => ({
    ...values,
    desktopBanner: '',
    mobileBanner: '',
    about: values.about || '',
    items: '',
  });

  const onSubmit = (values) => {
    if (isLoading) return;
    if (!client.status) {
      setMessageAlert({
        type: 'error',
        message: 'El cliente debe estar activo para crear una convocatoria.',
      });
      return;
    }
    const resolvedValues = getResolvedValues(values);

    dispatch({
      type: ACTION.UPDATE_LANDING,
      payload: {
        landing: getLandingPayload(resolvedValues.landing),
        status: status ? 'user-register' : 'waiting',
      },
    });
    dispatch({
      type: ACTION.UPDATE_LOGIN,
      payload: {
        login: resolvedValues.login,
      },
    });
    dispatch({
      type: ACTION.UPDATE_CONFIGURATION,
      payload: {
        configuration: resolvedValues.configuration,
      },
    });
    onNext();
  };

  const handlePreview = (values) => {
    const resolvedValues = getResolvedValues(values);
    const previewLanding = getLandingPayload(resolvedValues.landing);
    const previewLogin = resolvedValues.login;

    dispatch({
      type: ACTION.UPDATE_LANDING,
      payload: {
        landing: previewLanding,
      },
    });
    dispatch({
      type: ACTION.UPDATE_LOGIN,
      payload: {
        login: previewLogin,
      },
    });
    dispatch({
      type: ACTION.UPDATE_CONFIGURATION,
      payload: {
        configuration: resolvedValues.configuration,
      },
    });

    openAnnouncementWebPreview({
      previewType: 'login',
      previewState: {
        ...state,
        configuration: resolvedValues.configuration,
        landing: previewLanding,
        login: previewLogin,
      },
    });
  };

  const handlePrev = () => {
    setIsModalOpen(false);
    history.push(`/clientes/detalles`, {
      client: client.id,
    });
  };
  return (
    <>
      {messageAlert && (
        <Alert type={messageAlert.type}>{messageAlert.message}</Alert>
      )}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalContent
          onClick={() => setIsModalOpen(false)}
          title="Estás saliendo sin guardar los cambios"
        >
          <span className="title">¿Estás seguro que deseas volver?</span>
          <div className="modal__action-container">
            <Button
              type="button"
              secondary
              onClick={() => setIsModalOpen(false)}
            >
              No
            </Button>
            <Button type="button" onClick={handlePrev}>
              Si
            </Button>
          </div>
        </ModalContent>
      </Modal>
      <Formik
        initialValues={initialValues}
        validationSchema={landingSchema}
        validateOnBlur
        onSubmit={onSubmit}
        enableReinitialize
      >
        {(props) => {
          return (
            <>
              <Form className="announcement-form announcement-form--landing">
                <div className="wizard__preview-container announcement-form__toolbar">
                  <div className="announcement-form__toolbar-copy">
                    <span className="announcement-wizard__eyebrow">
                      Etapa 1 de 3
                    </span>
                    <h2>Landing de registro</h2>
                    <p>
                      Define la vigencia, los participantes y la información
                      pública de la convocatoria.
                    </p>
                  </div>
                  <Button
                    type="button"
                    secondary
                    className="button button--flat"
                    onClick={() => handlePreview(props.values)}
                  >
                    Previsualizar
                  </Button>
                </div>
                <div className="wizard__form">
                  <section className="announcement-form__card">
                    <div className="announcement-form__section-header">
                      <span className="announcement-form__section-icon">
                        <CalendarTodayOutlined />
                      </span>
                      <div>
                        <span className="announcement-wizard__eyebrow">
                          Configuración general
                        </span>
                        <h3>Vigencia y acceso</h3>
                        <p>
                          Selecciona las fechas de activación y los tipos de
                          usuario que podrán registrarse.
                        </p>
                      </div>
                    </div>
                    <div className="announcement-form__field-grid announcement-form__field-grid--two">
                      <InputWrapper label="Fecha de inicio" id="startDate">
                        <Input
                          label=""
                          id="startDate"
                          type="date"
                          name="startDate"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </InputWrapper>

                      <InputWrapper label="Fecha de término" id="endDate">
                        <Input
                          label=""
                          id="endDate"
                          type="date"
                          name="endDate"
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </InputWrapper>
                    </div>
                    <div className="announcement-form__field-grid">
                      <InputWrapper
                        label="Roles disponibles en el registro"
                        description="Selecciona los tipos de usuario que podrán registrarse en esta convocatoria."
                        id="configuration.registration.availableRoles"
                      >
                        <AvailableRolesSelect
                          name="configuration.registration.availableRoles"
                        />
                      </InputWrapper>
                    </div>
                  </section>

                  <section className="announcement-form__card">
                    <div className="announcement-form__section-header">
                      <span className="announcement-form__section-icon">
                        <WebOutlined />
                      </span>
                      <div>
                        <span className="announcement-wizard__eyebrow">
                          Identidad de la convocatoria
                        </span>
                        <h3>Contenido de la landing</h3>
                        <p>
                          Personaliza los textos y colores respetando la línea
                          gráfica del cliente y ChileConverge.
                        </p>
                      </div>
                    </div>

                    {client?.id && (
                      <div className="announcement-form__client-logo">
                        {announcementLogo !== DEFAULT_ANNOUNCEMENT_LOGO && (
                          <img
                            src={announcementLogo}
                            alt={`Logo de ${client.name || 'la convocatoria'}`}
                          />
                        )}
                        <div>
                          <strong>
                            {hasCustomLogo
                              ? 'Logo personalizado de la convocatoria'
                              : 'Logo del cliente'}
                          </strong>
                          <p>
                            {hasCustomLogo
                              ? 'Esta convocatoria tiene un logo personalizado que tiene prioridad sobre el logo del cliente.'
                              : 'El logo se hereda del cliente. Al cambiarlo en su ficha, se actualizará en sus convocatorias.'}
                          </p>
                          <Link
                            to={{
                              pathname: '/clientes/editar',
                              state: { client: client.id },
                            }}
                          >
                            Editar logo del cliente
                          </Link>
                        </div>
                      </div>
                    )}

                    <div className="announcement-form__field-grid">
                      <InputWrapper
                        label="Título de la convocatoria"
                        id="title"
                        description="Corresponde al llamado principal que aparece en el banner."
                      >
                        <Input name="title" label="Título" id="title" />
                        <span className="char-counter">
                          {(props.values.title || '').length}/{ALLOWED_CHAR}
                        </span>
                      </InputWrapper>
                      <InputWrapper
                        label="Bajada de la convocatoria"
                        id="mobileWelcomeMessage"
                        description="Este texto se muestra debajo del título en la vista mobile del login rediseñado."
                      >
                        <Input
                          name="mobileWelcomeMessage"
                          label="Bajada"
                          id="mobileWelcomeMessage"
                          max={MOBILE_WELCOME_MESSAGE_MAX_LENGTH}
                        />
                        <span className="char-counter">
                          {(props.values.mobileWelcomeMessage || '').length}/
                          {MOBILE_WELCOME_MESSAGE_MAX_LENGTH}
                        </span>
                      </InputWrapper>
                    </div>

                    <div className="announcement-form__field-grid">
                      <InputWrapper
                        label="Descripción del programa"
                        id="about"
                        name="about"
                        variant="big"
                        description="Este texto se mostrará bajo el nombre del programa en Voluntariado Coorporativo."
                      >
                        <>
                          <Editor
                            id="about"
                            name="about"
                            wrapperClassName="editor__wrapper"
                            toolbarClassName="editor__toolbar"
                            editorState={aboutEditor}
                            onEditorStateChange={(nextEditorState) => {
                              setAboutEditor(nextEditorState);
                              props.setFieldValue(
                                'about',
                                serializeEditorState(nextEditorState),
                                false
                              );
                            }}
                            onBlur={() => props.setFieldTouched('about', true)}
                          />
                          <div className="editor__error">
                            {props.touched.about && props.errors.about && (
                              <span className="error-message">
                                {props.errors.about}
                              </span>
                            )}
                            <span className="char-counter">
                              {aboutEditor
                                .getCurrentContent()
                                .getPlainText('').length}
                              /{PROGRAM_DESCRIPTION_MAX_LENGTH}
                            </span>
                          </div>
                        </>
                      </InputWrapper>
                    </div>

                    <div className="announcement-form__subsection">
                      <div className="announcement-form__subsection-heading">
                        <span className="announcement-wizard__eyebrow">
                          Paleta del acceso
                        </span>
                        <strong>Colores del login y registro</strong>
                      </div>
                      <div className="announcement-form__field-grid announcement-form__field-grid--two">
                        <InputWrapper
                          variant="full"
                          label="Color primario del login y registro"
                          id="loginPrimaryColor"
                        >
                          <div className="preview__color-container">
                            <InputColor name="loginPrimaryColor" />
                            <div className="description-container">
                              <InfoOutlined className="info_svg" />
                              <span className="description">
                                Se aplica a los botones y acentos principales del
                                login y registro rediseñado de la convocatoria.
                              </span>
                            </div>
                          </div>
                        </InputWrapper>
                        <InputWrapper
                          variant="full"
                          label="Color secundario del login y registro"
                          id="loginSecondaryColor"
                        >
                          <div className="preview__color-container">
                            <InputColor name="loginSecondaryColor" />
                            <div className="description-container">
                              <InfoOutlined className="info_svg" />
                              <span className="description">
                                Se aplica a links, indicadores y acentos
                                secundarios del login y registro rediseñado.
                              </span>
                            </div>
                          </div>
                        </InputWrapper>
                      </div>
                    </div>
                  </section>
                  <div className="wizard__action">
                    <Button secondary onClick={() => setIsModalOpen(true)}>
                      Volver
                    </Button>
                    <Button
                      type="submit"
                      loading={isLoading}
                      disabled={isLoading}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default LandingForm;
